import { useCallback, useEffect, useState } from 'react';
import { ApiError, fetchJoinGrant, fetchMe, signIn } from './api';
import type { JoinGrant, Me } from './api';
import { Call } from './Call';
import { Dashboard } from './Dashboard';
import { MeetingDetail, Meetings } from './Meetings';
import { Player } from './Player';
import { Rooms } from './Rooms';
import { Schedule } from './Schedule';
import { Search } from './Search';
import { Admin } from './Admin';
import { Diagnostics } from './Diagnostics';
import { AppShell } from './AppShell';
import { Mark } from './Mark';
import { brand } from './brand';
import { hrefFor, parseRoute } from './routes';
import type { Route } from './routes';

/** A centred card — the landing, loading and error states all wear one. */
function Card({
  title,
  children,
  action,
}: {
  title: string;
  children?: React.ReactNode;
  action?: React.ReactNode;
}) {
  return (
    <div className="mx-centre">
      <div className="mx-card">
        <Mark />
        <h1>{title}</h1>
        {children}
        {action}
      </div>
    </div>
  );
}

/**
 * Joining a room: fetch a grant, then hand it to the call.
 *
 * Split out of App because it is the ONLY place that mints a way into a meeting,
 * and it now runs only when someone has navigated to /call. It used to run on
 * mount for every visit to the site, which is what made the front door put people
 * on camera.
 */
function JoinRoom({
  room,
  me,
  navigate,
}: {
  room: string;
  me: Me;
  navigate: (r: Route) => void;
}) {
  const [grant, setGrant] = useState<JoinGrant | null>(null);
  const [error, setError] = useState<{ message: string; canRetry: boolean } | null>(null);

  const join = useCallback(() => {
    setError(null);
    // An empty room means "no preference" and the portal resolves it to the
    // default; it answers with the room it actually chose.
    fetchJoinGrant(room)
      .then((g) => {
        setGrant(g);
        // Put the resolved room in the address bar so a refresh, a bookmark or a
        // copied URL lands in the same meeting rather than back at "no preference".
        if (g.room && g.room !== room) {
          window.history.replaceState({}, '', hrefFor({ name: 'call', room: g.room }));
        }
      })
      .catch((e: unknown) => {
        // A 401 means the session expired between /api/me and /api/token. Sign in
        // again rather than retrying the same request.
        if (e instanceof ApiError && e.status === 401) {
          signIn();
          return;
        }
        setError({
          message: e instanceof Error ? e.message : String(e),
          canRetry: !(e instanceof ApiError && e.status === 404),
        });
      });
  }, [room]);

  useEffect(join, [join]);

  if (error) {
    return (
      <Card
        title="Could not join"
        action={
          error.canRetry ? (
            <button className="mx-button" onClick={join}>
              Try again
            </button>
          ) : (
            <button className="mx-button" onClick={() => navigate({ name: 'dashboard' })}>
              Back to {brand().shortName}
            </button>
          )
        }
      >
        <p className="mx-error">{error.message}</p>
      </Card>
    );
  }
  if (!grant) return <Card title="Connecting…" />;

  return (
    <Call
      grant={grant}
      me={me.name ?? me.email ?? ''}
      // Leaving goes back to the dashboard rather than to a dead-end card: from
      // there the room is one click away again, and the summary of what just
      // happened turns up on the same page a few minutes later…
      // ...but ONLY if that is why the room went away. Navigating to Meetings
      // unmounts LiveKitRoom, which disconnects, which fires this — and without
      // the check that redirect would fight the one the user just asked for and
      // win. pushState has already moved the URL by then, so the URL is the
      // reliable answer to "did they leave, or did they go somewhere?".
      onLeave={() => {
        if (parseRoute().name === 'call') navigate({ name: 'dashboard' });
      }}
      navigate={navigate}
    />
  );
}

export function App() {
  const [route, setRoute] = useState<Route>(() => parseRoute());
  const [me, setMe] = useState<Me | null>(null);
  const [error, setError] = useState<string | null>(null);

  // The back button. Without this, every in-app navigation was a one-way trip:
  // pushState changed the URL and nothing re-rendered when the browser popped it.
  useEffect(() => {
    const onPop = () => setRoute(parseRoute());
    window.addEventListener('popstate', onPop);
    return () => window.removeEventListener('popstate', onPop);
  }, []);

  const navigate = useCallback((r: Route) => {
    window.history.pushState({}, '', hrefFor(r));
    setRoute(r);
  }, []);

  useEffect(() => {
    fetchMe()
      .then(setMe)
      .catch((e: unknown) => setError(e instanceof Error ? e.message : String(e)));
  }, []);

  if (error) {
    return (
      <Card
        title="Something went wrong"
        action={
          <button className="mx-button" onClick={() => window.location.reload()}>
            Try again
          </button>
        }
      >
        <p className="mx-error">{error}</p>
      </Card>
    );
  }

  // A GUEST'S SUMMARY LINK MUST NOT ASK FOR AN ACCOUNT.
  //
  // They joined a meeting on an invite, gave an address, and were emailed a
  // signed link to that one meeting's summary. There is no account behind it and
  // never will be, so sending them to Keycloak is sending them nowhere. The
  // token in the URL is the permission; the server checks it against the meeting
  // being asked for. This is deliberately BEFORE the authentication gate and
  // deliberately the only route that skips it.
  // The same for playing it back (MN-35): the guest's link carries the token.
  if (route.name === 'play' && route.token) {
    return (
      <AppShell route={route} navigate={navigate} title="Meeting playback" mainClass="mx-pb-shell">
        <Player
          room={route.room}
          session={route.session}
          token={route.token}
          at={route.at}
          navigate={navigate}
        />
      </AppShell>
    );
  }

  if (route.name === 'meeting' && route.token) {
    return (
      // No `me`: there is no account behind the link, and the shell renders the
      // rail with no destinations and no account menu rather than inventing
      // either. They still get the frame, so what they were sent looks like part
      // of a product instead of a bare page.
      <AppShell route={route} navigate={navigate} title="Meeting summary">
        <MeetingDetail room={route.room} session={route.session} token={route.token} />
      </AppShell>
    );
  }

  if (!me) return <Card title={brand().name} />;

  if (!me.authenticated) {
    return (
      <Card
        title={brand().name}
        action={
          <button className="mx-button" onClick={() => signIn()}>
            {brand().signInLabel}
          </button>
        }
      >
        <p>
          Meetings, summaries and transcripts are open to {brand().audience}. Signing in takes you
          through single sign-on — there is no meeting password.
        </p>
      </Card>
    );
  }

  // THE CALL IS THE ONE ROUTE OUTSIDE THE FRAME. It wants the whole window, and
  // every nav row beside it would be a way to hang up by accident — see the
  // header of AppShell.tsx.
  if (route.name === 'call') return <JoinRoom room={route.room} me={me} navigate={navigate} />;

  const shell = (title: string, body: React.ReactNode, mainClass?: string) => (
    <AppShell me={me} route={route} navigate={navigate} title={title} mainClass={mainClass}>
      {body}
    </AppShell>
  );

  switch (route.name) {
    case 'meetings':
      return shell('Meetings', <Meetings open={null} isOp={me.op} navigate={navigate} />);

    case 'meeting':
      return shell(
        'Meetings',
        <Meetings
          open={{ room: route.room, session: route.session, token: route.token, at: route.at }}
          isOp={me.op}
          navigate={navigate}
        />,
      );

    case 'play':
      return shell(
        'Meetings',
        <Player room={route.room} session={route.session} at={route.at} navigate={navigate} />,
        'mx-pb-shell',
      );

    case 'schedule':
      return shell('Schedule', <Schedule navigate={navigate} />, 'mx-dash');

    case 'rooms':
      return shell('Rooms', <Rooms navigate={navigate} />, 'mx-dash');

    case 'search':
      return shell(
        'Search',
        <Search
          initial={{ q: route.q, speaker: route.speaker, room: route.room }}
          navigate={navigate}
        />,
        'mx-search',
      );

    case 'admin':
      return shell('Governance', <Admin isOp={!!me.op} />, 'mx-dash');

    case 'diagnostics':
      return shell('Status', <Diagnostics />);

    case 'dashboard':
      // 'Home', not the brand name: the wordmark is in the rail immediately to
      // the left of this, and printing it twice side by side reads as a bug.
      return shell('Home', <Dashboard me={me} navigate={navigate} />, 'mx-dash');
  }
}
